import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
// import { CharterSignatureCounter } from "./CharterSignatureCounter"; // Hidden for now
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import chartePreview from "@/assets/charte-preview.png";
import { CharteSignatureForm } from "./CharteSignatureForm";

export const Vision = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPdfUrl = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: files, error: filesError } = await supabase.storage.from('manifeste').list();
      if (filesError) {
        console.error('Error listing files in "manifeste" bucket:', filesError);
        throw new Error('Erreur lors de la vérification des fichiers');
      }
      console.log('Files in manifeste bucket:', files);
      const { data, error } = await supabase.storage.from('manifeste').createSignedUrl('ManifesteLEDLR.pdf', 3600);
      if (error) {
        console.error('Error creating signed URL:', error);
        throw error;
      }
      if (!data || !data.signedUrl) {
        throw new Error('Échec de génération de l\'URL signée');
      }
      console.log('PDF URL generated successfully');
      setPdfUrl(data.signedUrl);
    } catch (err) {
      console.error('Error fetching PDF:', err);
      setError('Impossible de charger le manifeste. Veuillez télécharger le fichier ManifesteLEDLR.pdf dans le bucket "manifeste".');
      toast.error("Erreur de chargement", {
        description: "Le document n'a pas pu être chargé. Veuillez télécharger le fichier ManifesteLEDLR.pdf dans le bucket manifeste."
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDialogOpen && !pdfUrl) {
      fetchPdfUrl();
    }
  }, [isDialogOpen, pdfUrl]);

  return (
    <section className="pt-12 pb-2 bg-gradient-to-b from-[#FFFD4C] via-[#FFFD4C] to-[#F5E84A]">
      <div className="container px-4 md:px-6 max-w-6xl mx-auto">
        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {/* Left: Manifeste card */}
          <div className="md:col-span-2 bg-gray-200/80 backdrop-blur-sm rounded-2xl p-5 md:p-6 shadow-sm text-[#1E3A8A] [&_*]:text-[#1E3A8A] leading-snug text-[14px] space-y-3">
            <h2 className="text-xl font-bold text-center mb-4">
              Manifeste des Enfants de la République
            </h2>
            <p className="text-justify">
              Il est des promesses qui façonnent les peuples et des idéaux qui traversent le temps. La République française est de ceux-là car la France n'est pas une nation comme les autres : sa démocratie est aussi une République. Au fonctionnement de ses institutions et de ses lois formant un état de droit, s'ajoutent un corpus de valeurs, la faisant devenir un souffle, un projet de civilisation, une promesse faite à tous ses enfants d'une liberté inaliénable, d'une égalité intangible, d'une fraternité indéfectible et d'une laïcité garante d'un avenir collectif.
            </p>
            <p className="text-justify">
              Cette promesse, si précieuse, cet horizon commun, vacille aujourd'hui, attaqué ouvertement par les extrémismes de tout bord. Contestée à l'extérieur par des forces qui rêvent d'en finir avec son modèle universaliste, minée à l'intérieur par des courants d'intérêts particuliers qui la jugent dépassée, discriminante ou oppressive, la République française se trouve affaiblie et en danger.
            </p>
            <p className="font-semibold pt-1">Un idéal à raviver</p>
            <p className="text-justify">
              Nous, Enfants de la République, comme d'autres citoyens, refusons de rester spectateurs. Nous sommes issus de tous horizons, professions et origines, unis par un seul engagement : faire vivre et défendre cet idéal républicain que nous avons reçu en héritage et que nous nous devons de transmettre à nos générations futures.
            </p>
            <p className="text-justify">
              La République est un processus vivant. Elle se nourrit de l'engagement de ses citoyens, de leur attachement à ses valeurs, à ses lois, synthétisées dans la Charte des droits et des devoirs du citoyen français (article 21-24 du code civil – JO du 31 janvier 2012).
            </p>
          </div>

          {/* Right: charte image + signature card */}
          <div className="flex flex-col gap-4 h-full justify-between">
            <a
              href="https://ledlr.org/Charte_des_droits_et_devoirs_du_citoyen_francais-juin-2020.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="block hover:opacity-90 transition-opacity rounded-2xl overflow-hidden shadow-lg border border-black/20"
            >
              <img
                src={chartePreview}
                alt="Charte des droits et devoirs du citoyen français"
                className="w-full h-64 md:h-72 object-cover object-top block"
              />
            </a>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <div className="bg-[#FFFD4C] border border-black/20 rounded-2xl p-4 shadow-lg cursor-pointer hover:bg-[#F5E84A] transition-colors">
                  <h3 className="text-base font-semibold mb-1 text-[#1E3A8A]">Signez la charte</h3>
                  <p className="text-[13px] leading-snug text-[#1E3A8A] mb-3">
                    Soutenez l'action des Enfants de la République en signant La Charte. Vous recevrez gratuitement votre charte "Enfants de la République" personnalisée et numérotée !
                  </p>
                  <button className="w-full bg-[#1E3A8A] text-white py-2 px-4 rounded-full font-semibold text-sm hover:bg-[#1E3A8A]/90 transition-colors">
                    Je signe la charte
                  </button>
                </div>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Signer la charte républicaine</DialogTitle>
                </DialogHeader>
                <CharteSignatureForm onSuccess={() => setIsDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </section>
  );
};
